import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CustomModal from '../../../util_components/custom_modal';
import ConfirmationPopup from '../../../util_components/confirmation_popup';
import { form_from_leg, is_valid_trial_points_exception } from './trial_points_exception_rules';

const ROLE_LABELS = { delivered: 'Delivered', replaced_off: 'Replaced off' };

const option_label_style = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  margin: 0,
  textTransform: 'none',
  fontWeight: 'normal',
  color: 'inherit'
};

const field_label_style = { ...option_label_style, cursor: 'default', flexWrap: 'wrap' };

const forms_from = (legs) =>
  (legs || []).reduce((acc, leg) => ({ ...acc, [leg.teacher_uuid]: form_from_leg(leg) }), {});

const same_form = (a, b) => Object.keys(a).every((key) => a[key] === b[key]);

const exception_summary = (ex) =>
  ex.is_waived === 1 ? 'Waived (no points deducted)' : `Custom charge ${ex.custom_charge}`;

// PROD-9786: one row per teacher who owes a deduction; one Save or Clear in flight at a time (D-22).
function TrialPointsExceptionLegs({ legs, loading, writing, on_save, on_clear }) {
  const [forms, set_forms] = useState(() => forms_from(legs));
  const [pending, set_pending] = useState(null);
  // The leg whose Clear waits for a Yes in the confirmation popup.
  const [confirm_leg, set_confirm_leg] = useState(null);
  // Drops a second click that lands before the disabled re-render.
  const in_flight = useRef(false);
  // Server forms the rows were last seeded from; a row that differs has unsaved edits.
  const seeded = useRef(forms_from(legs));
  // Teacher whose Save or Clear ran last; that row reseeds on the next reload.
  const written_uuid = useRef(null);

  // A reload keeps other rows' unsaved edits; saved, new and untouched rows take server values.
  useEffect(() => {
    const server = forms_from(legs);
    const base = seeded.current;
    const written = written_uuid.current;
    seeded.current = server;
    written_uuid.current = null;
    set_forms((prev) =>
      Object.keys(server).reduce((acc, id) => {
        const edited = id !== written && prev[id] && base[id] && !same_form(prev[id], base[id]);
        return { ...acc, [id]: edited ? prev[id] : server[id] };
      }, {})
    );
  }, [legs]);

  const run = (teacher_uuid, fn) => {
    if (in_flight.current || writing) return;
    in_flight.current = true;
    written_uuid.current = teacher_uuid;
    set_pending(teacher_uuid);
    // Calls fn now; a sync throw becomes a rejection.
    new Promise((resolve) => {
      resolve(fn());
    })
      // index.jsx notifies; the guard only needs the settle.
      .catch(() => {})
      .finally(() => {
        in_flight.current = false;
        set_pending(null);
      });
  };

  const update_form = (teacher_uuid, change) =>
    set_forms((prev) => ({
      ...prev,
      [teacher_uuid]: { ...prev[teacher_uuid], ...change(prev[teacher_uuid]) }
    }));

  // Ticking Waived clears the custom charge, which the backend drops anyway.
  const toggle_waived = (teacher_uuid) =>
    update_form(teacher_uuid, (f) => ({
      waived: !f.waived,
      custom_enabled: f.waived ? f.custom_enabled : false
    }));

  const toggle_custom = (teacher_uuid) =>
    update_form(teacher_uuid, (f) => ({
      custom_enabled: !f.custom_enabled,
      charge: f.custom_enabled ? '' : f.charge
    }));

  if (loading) {
    return <div className="p-4">Loading the current exceptions...</div>;
  }
  if (!legs || legs.length === 0) {
    return <div className="p-4">No deduction to override on this session.</div>;
  }

  // `writing` is the page's flag, so a reopened modal still waits for the earlier write.
  const busy = pending !== null || writing;

  const render_leg = (leg) => {
    const id = leg.teacher_uuid;
    const form = forms[id] || form_from_leg(leg);
    const name = leg.teacher_name || leg.teacher_uuid;
    return (
      <div
        key={id}
        role="group"
        aria-label={name}
        style={{
          margin: '12px 0',
          padding: '10px 12px',
          border: '1px solid var(--border-default)'
        }}
      >
        <div style={{ fontSize: '14px' }}>
          <b>{name}</b> · {ROLE_LABELS[leg.role] || leg.role} · {leg.outcome_text || '—'} · Points
          charged: {leg.points_charged}
        </div>

        {leg.exception ? (
          <div
            style={{
              margin: '8px 0',
              padding: '6px 10px',
              borderLeft: '3px solid var(--brand-secondary)',
              background: 'var(--brand-primary-subtle-bg)',
              fontSize: '13px'
            }}
          >
            <b>Exception applied:</b> {exception_summary(leg.exception)}
            <br />
            <b>Reason:</b> {leg.exception.reason}
          </div>
        ) : null}

        <div style={{ margin: '8px 0' }}>
          <label htmlFor={`${id}-waived`} style={option_label_style}>
            <input
              id={`${id}-waived`}
              type="checkbox"
              checked={form.waived}
              disabled={busy}
              onChange={() => toggle_waived(id)}
              style={{ margin: 0 }}
            />
            <span>Waived (no points deducted)</span>
          </label>
        </div>

        {/* Hidden under a waiver; its own checkbox keeps an empty box from reading as zero. */}
        {!form.waived ? (
          <div style={{ margin: '8px 0' }}>
            <label htmlFor={`${id}-custom`} style={option_label_style}>
              <input
                id={`${id}-custom`}
                type="checkbox"
                checked={form.custom_enabled}
                disabled={busy}
                onChange={() => toggle_custom(id)}
                style={{ margin: 0 }}
              />
              <span>Custom charge</span>
            </label>
            {form.custom_enabled ? (
              <label
                htmlFor={`${id}-charge`}
                style={{ ...field_label_style, margin: '8px 0 0 24px' }}
              >
                <span>Custom charge (0 to 10)</span>
                <input
                  id={`${id}-charge`}
                  type="number"
                  value={form.charge}
                  disabled={busy}
                  onChange={(e) => {
                    const { value } = e.target;
                    update_form(id, () => ({ charge: value }));
                  }}
                  style={{ width: '120px' }}
                />
              </label>
            ) : null}
          </div>
        ) : null}

        <label htmlFor={`${id}-reason`} style={{ ...field_label_style, margin: '8px 0' }}>
          <span>Reason (required)</span>
          <input
            id={`${id}-reason`}
            value={form.reason}
            disabled={busy}
            onChange={(e) => {
              const { value } = e.target;
              update_form(id, () => ({ reason: value }));
            }}
            style={{ width: '360px' }}
          />
        </label>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            disabled={busy || !is_valid_trial_points_exception(form)}
            onClick={() => run(id, () => on_save(leg, form))}
          >
            {pending === id ? 'Saving...' : 'Save'}
          </button>
          {leg.exception ? (
            <button type="button" disabled={busy} onClick={() => set_confirm_leg(leg)}>
              Clear
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const confirm_clear = () => {
    const leg = confirm_leg;
    set_confirm_leg(null);
    if (leg) run(leg.teacher_uuid, () => on_clear(leg));
  };

  return (
    <div>
      {legs.map(render_leg)}
      <CustomModal show={!!confirm_leg} close={() => set_confirm_leg(null)}>
        <ConfirmationPopup
          title={
            confirm_leg
              ? `Clear the exception for ${confirm_leg.teacher_name || confirm_leg.teacher_uuid}? Their standard deduction will apply again.`
              : ''
          }
          close_modal={() => set_confirm_leg(null)}
          confirm={confirm_clear}
        />
      </CustomModal>
    </div>
  );
}

TrialPointsExceptionLegs.propTypes = {
  legs: PropTypes.arrayOf(
    PropTypes.shape({
      teacher_uuid: PropTypes.string.isRequired,
      teacher_name: PropTypes.string,
      role: PropTypes.string,
      outcome_text: PropTypes.string,
      points_charged: PropTypes.number,
      exception: PropTypes.shape({
        is_waived: PropTypes.number,
        custom_charge: PropTypes.number,
        reason: PropTypes.string
      })
    })
  ),
  loading: PropTypes.bool,
  writing: PropTypes.bool,
  on_save: PropTypes.func.isRequired,
  on_clear: PropTypes.func.isRequired
};

TrialPointsExceptionLegs.defaultProps = {
  legs: [],
  loading: false,
  writing: false
};

export default TrialPointsExceptionLegs;
